import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { get, getToken } from '../services/api';
import { Satellite, RefreshCw, Radio, Circle, Map, Cloud, Layers, Maximize2, Minimize2, Ship } from 'lucide-react';
import VesselTrailMap from '../components/VesselTrailMap';
import WindyMap from '../components/WindyMap';
import { hasFeature } from '../utils/featureFlags';
import TimelineSlider from '../components/TimelineSlider';
import CrewStatusPanel, { CrewRecord } from '../components/CrewStatusPanel';
import type { VesselData, TrailPoint, MapType, PortMarker } from '../components/VesselTrailMap';

interface WorkSession {
  userId: string;
  startedAt: string;
  endedAt: string | null;
  sessionStatus: string;
  workType: string;
  totalWorkMinutes: number;
}

interface CrewBase {
  id: string;
  name: string;
  role: string;
  position: string;
  boardedAt: string;
}

interface GpsVessel {
  id: string;
  name: string;
  code: string;
  status: string;
  color: string;
  lat: number;
  lng: number;
  speed: number;
  heading: number;
  positionUnknown: boolean;
  trail: TrailPoint[];
  crew: CrewBase[];
  sessions: WorkSession[];
}

function getCrewAtTime(crew: CrewBase[], sessions: WorkSession[], atMs: number): CrewRecord[] {
  const atIso = new Date(atMs).toISOString();
  return crew.map(c => {
    const active = sessions
      .filter(s => s.userId === c.id && s.startedAt <= atIso && (s.endedAt === null || s.endedAt > atIso))
      .sort((a, b) => b.startedAt.localeCompare(a.startedAt))[0] ?? null;
    return {
      id:       c.id,
      name:     c.name,
      role:     c.role,
      position: c.position,
      boardedAt: c.boardedAt,
      // session が見つかれば「その時刻に作業していた」→勤務中 or 休憩中
      // completed セッションでもその時刻に活動中であれば勤務中として表示
      status:    active
               ? (active.sessionStatus === 'resting' ? 'resting' : 'working')
               : 'standby',
      workType:        active?.workType ?? '',
      cumulativeHours: active ? Math.round((active.totalWorkMinutes / 60) * 10) / 10 : 0,
    } as CrewRecord;
  });
}

/**
 * AIS を開ける外部サービス。座標を渡してその海域を中心に表示する。
 * 埋め込み（iframe）は各社が拒否するため、必ず新しいタブで開く。
 */
const AIS_PROVIDERS: { label: string; note: string; url: (lat: number, lon: number) => string }[] = [
  {
    label: 'MarineTraffic',
    note: '世界の船舶を網羅。無料でも周辺の他船を確認できる',
    url: (lat, lon) => `https://www.marinetraffic.com/en/ais/home/centerx:${lon.toFixed(4)}/centery:${lat.toFixed(4)}/zoom:11`,
  },
  {
    label: 'VesselFinder',
    note: '軽量で見やすい。船名検索も可能',
    url: (lat, lon) => `https://www.vesselfinder.com/?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}&zoom=11`,
  },
  {
    label: '海しる（海洋状況表示システム）',
    note: '海上保安庁。航行警報・潮流など日本の公式情報',
    url: (lat, lon) => `https://www.msil.go.jp/msilgisapi/Map/Index?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}&zoom=11`,
  },
];

export default function GpsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [vessels, setVessels]           = useState<GpsVessel[]>([]);
  const [selectedId, setSelectedId]     = useState<string | null>(() => searchParams.get('vessel') ?? null);
  const [loading, setLoading]           = useState(true);
  const [isPlaying, setIsPlaying]       = useState(false);
  const [speed, setSpeed]               = useState(1);
  const VALID_PERIODS = ['24h', '3d', '1w', '2w', '1M', '3M'] as const;
  const [period, setPeriod]             = useState<'24h' | '3d' | '1w' | '2w' | '1M' | '3M'>(() => {
    const p = searchParams.get('period');
    return (VALID_PERIODS as readonly string[]).includes(p ?? '') ? p as typeof VALID_PERIODS[number] : '24h';
  });
  const [sliderTs, setSliderTs]         = useState<number>(Date.now());
  const [rangeMin, setRangeMin]         = useState<number>(Date.now() - 3_600_000 * 24);
  const [rangeMax, setRangeMax]         = useState<number>(Date.now());
  const [showTrail, setShowTrail]       = useState(true);
  const [showLabel, setShowLabel]       = useState(true);
  const [lastUpdated, setLastUpdated]   = useState<Date | null>(null);
  const [mapType, setMapType]                 = useState<MapType>('standard');
  const [showMaritimeZones, setShowMaritimeZones] = useState(true);
  const [showWindy, setShowWindy]             = useState(false);
  const [showAis, setShowAis]                 = useState(false);
  /**
   * Windy 気象レイヤー（windy_map）。
   * embed.windy.com の埋め込みは Windy 一般利用規約でメディア向けと定められ、
   * 「weather apps and other commercial websites」での利用が認められていない。
   * Map Forecast API（Professional）の契約が済むまで全社 OFF にしておく。
   * 契約後は iframe を API 版（WindyMap）へ差し替えたうえでフラグを ON にする。
   */
  const windyEnabled = hasFeature('windy_map')
    // 開発時はキーがあれば動かせる（契約前の検証用。本番ビルドでは DEV=false なので無効）
    || (import.meta.env.DEV && !!import.meta.env.VITE_WINDY_KEY);
  // AIS地図の初期中心（開いた時点の選択船舶の位置で固定し、60秒ポーリングでのiframe再読込を防ぐ）
  const [aisCenter, setAisCenter]             = useState<{ lat: number; lng: number }>({ lat: 34.7, lng: 135.0 });
  const [isFullscreen, setIsFullscreen]       = useState(false);
  const [msilLayers, setMsilLayers]           = useState<Set<string>>(new Set());
  const [showLayerMenu, setShowLayerMenu]     = useState(false);
  const [ports, setPorts]                     = useState<PortMarker[]>([]);
  const [showPorts, setShowPorts]             = useState(false);
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const apiBase = (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api').replace(/\/api$/, '');
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // フルスクリーン状態の同期
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);


  useEffect(() => {
    const p: Record<string, string> = { period };
    if (selectedId) p.vessel = selectedId;
    setSearchParams(p, { replace: true });
  }, [period, selectedId, setSearchParams]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapWrapperRef.current?.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  };


  const fetchVessels = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const PERIOD_HOURS: Record<string, number> = { '24h': 24, '3d': 72, '1w': 168, '2w': 336, '1M': 720, '3M': 2160 };
      const periodHours = PERIOD_HOURS[period] ?? 24;

      const data = await get<any>('/vessels/gps/trail', { hours: String(periodHours) });
      const raw: any[] = data?.vessels ?? data?.data ?? [];

      const mapped: GpsVessel[] = raw.map((v: any) => {
        const trail: TrailPoint[] = (v.trail ?? []).map((p: any) => ({
          ts:      Number(p.ts ?? p.timestamp ?? 0),
          lat:     Number(p.lat ?? p.latitude  ?? 0),
          lng:     Number(p.lng ?? p.longitude ?? 0),
          speed:   Number(p.speed   ?? 0),
          heading: Number(p.heading ?? 0),
        }));
        const status = String(v.status ?? 'offline');
        // 色は運航状態（打刻・GPSからの自動判定）に連動させる
        const STATUS_COLOR: Record<string, string> = {
          sailing: '#0EA5E9', port: '#22C55E', anchored: '#94A3B8', offline: '#64748B',
        };
        const color = STATUS_COLOR[status] ?? '#94A3B8';
        return {
          id:      String(v.id ?? v.vessel_id ?? ''),
          name:    String(v.vessel_name ?? v.name ?? ''),
          code:    String(v.vessel_code ?? v.code ?? ''),
          status,
          color,
          positionUnknown: Boolean(v.position_unknown),
          lat:     v.position_unknown ? NaN : Number(v.lat ?? v.latitude  ?? 35.0),
          lng:     v.position_unknown ? NaN : Number(v.lng ?? v.longitude ?? 136.0),
          speed:   Number(v.speed   ?? 0),
          heading: Number(v.heading ?? 0),
          trail,
          crew: (v.crew ?? []).map((c: any) => ({
            id:       String(c.id ?? c.user_id ?? ''),
            name:     String(c.name ?? c.user_name ?? ''),
            role:     String(c.role ?? 'crew'),
            position: String(c.position ?? ''),
            boardedAt: String(c.boardedAt ?? c.boarded_at ?? ''),
          })),
          sessions: (v.sessions ?? []).map((s: any) => ({
            userId:       String(s.userId ?? s.user_id ?? ''),
            startedAt:    String(s.startedAt ?? s.started_at ?? ''),
            endedAt:      s.endedAt ?? s.ended_at ?? null,
            sessionStatus: String(s.sessionStatus ?? s.session_status ?? ''),
            workType:     String(s.workType ?? s.work_type ?? ''),
            totalWorkMinutes: Number(s.totalWorkMinutes ?? s.total_work_minutes ?? 0),
          })),
        };
      });

      setVessels(mapped);
      // URLに指定の船舶があればそれを選択、なければ最初の船舶（ロード後に一度だけ）
      setSelectedId(prev => {
        if (prev !== null && mapped.some(v => v.id === prev)) return prev;
        return mapped[0]?.id ?? null;
      });

      const allTs = mapped.flatMap(v => v.trail.map(p => p.ts)).filter(Boolean);
      if (allTs.length > 0) {
        const mn = Math.min(...allTs);
        const mx = Math.max(...allTs);
        setRangeMin(mn);
        setRangeMax(mx);
        setSliderTs(mx);
      }
      setLastUpdated(new Date());
    } catch {
      if (!silent) setVessels([]);
    }
    if (!silent) setLoading(false);
  }, [period]);

  useEffect(() => {
    fetchVessels();
    const timer = setInterval(() => fetchVessels(true), 60_000);
    return () => clearInterval(timer);
  }, [fetchVessels]);

  // 寄港地マスタ取得（showPortsがONになったとき一度だけ）
  useEffect(() => {
    if (!showPorts || ports.length > 0) return;
    const token = getToken() ?? '';
    const subdomain = (() => {
      const h = window.location.hostname;
      if (h !== 'localhost' && h !== '127.0.0.1') { const p = h.split('.'); if (p.length >= 3 && p[0]) return p[0]; }
      return (import.meta.env.VITE_COMPANY_SUBDOMAIN as string | undefined) ?? 'unison-tech';
    })();
    fetch(`${apiBase}/api/port-masters`, {
      headers: { Authorization: `Bearer ${token}`, 'X-Company-Subdomain': subdomain, 'X-App-Type': 'admin' },
    })
      .then(r => r.json())
      .then(d => setPorts((d?.data ?? []).filter((p: any) => p.is_active)))
      .catch(err => console.error('[GpsPage] port-masters fetch failed:', err));
  }, [showPorts, apiBase, ports.length]);

  // 再生：全期間を約300ステップで再生（期間に比例したステップ幅）
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setSliderTs(prev => {
          // 期間全体を約300ステップ・300ms = ~90秒で再生。最小5分、最大6時間
          const totalRange = rangeMax - rangeMin;
          const baseStep = Math.min(
            6 * 3600_000,              // 上限 6時間
            Math.max(5 * 60_000, Math.round(totalRange / 300)) // 下限 5分
          );
          const step = baseStep * speed;
          const next = prev + step;
          if (next >= rangeMax) { setIsPlaying(false); return rangeMax; }
          return next;
        });
      }, 300);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [isPlaying, rangeMin, rangeMax, speed]);

  // 期間が長いほど表示ポイントを間引く（地図描画の軽量化）
  const DECIMATE: Record<string, number> = { '24h': 1, '3d': 2, '1w': 4, '2w': 8, '1M': 20, '3M': 60 };
  const decimateFactor = DECIMATE[period] ?? 1;

  const vesselData: VesselData[] = vessels.map(v => ({
    id: v.id, name: v.name, code: v.code, status: v.status, color: v.color,
    trail: decimateFactor === 1 ? v.trail : v.trail.filter((_, i) => i % decimateFactor === 0),
  }));

  // タイムラインスライダーの時刻に基づいて各船の勤務状況を動的計算
  const vesselCrewAtTime = useMemo(() =>
    Object.fromEntries(vessels.map(v => [v.id, getCrewAtTime(v.crew, v.sessions, sliderTs)])),
    [vessels, sliderTs]
  );

  const selected = vessels.find(v => v.id === selectedId) ?? null;
  const selectedVessel = selected
    ? { id: selected.id, name: selected.name, code: selected.code }
    : null;
  const dynamicCrew = selectedId ? (vesselCrewAtTime[selectedId] ?? []) : [];


  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#F4F6F8' }}>

      {/* ヘッダー */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #E5E7EB',
        padding: '12px 24px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 30, height: 30, borderRadius: 8, background: '#EBF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Satellite size={16} color="#0066CC" />
          </span>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>GPS位置情報</h1>
        </div>

        {/* 表示設定 */}
        <div style={{ display: 'flex', gap: 12, marginLeft: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { key: 'trail', label: '航跡', val: showTrail, set: setShowTrail },
            { key: 'label', label: 'ラベル', val: showLabel, set: setShowLabel },
            { key: 'zones', label: '海域区分', val: showMaritimeZones, set: setShowMaritimeZones },
          ].map(({ key, label, val, set }) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={val} onChange={e => set(e.target.checked)}
                style={{ accentColor: '#0066CC', width: 14, height: 14, cursor: 'pointer' }} />
              {label}
            </label>
          ))}

          {/* 地図タイプ切り替え */}
          <div style={{ display: 'flex', gap: 2, background: '#f3f4f6', borderRadius: 7, padding: 3, marginLeft: 4 }}>
            {([
              { key: 'standard', label: '標準', icon: <Map size={12} /> },
              { key: 'satellite', label: '衛星', icon: <Satellite size={12} /> },
            ] as const).map(({ key, label, icon }) => (
              <button key={key} onClick={() => setMapType(key)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '4px 10px', borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: mapType === key ? '#fff' : 'transparent',
                color: mapType === key ? '#0066CC' : '#6b7280',
                boxShadow: mapType === key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all .15s',
              }}>{icon}{label}</button>
            ))}
          </div>

          {/* MSIL レイヤーコントロール */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowLayerMenu(p => !p)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: msilLayers.size > 0 ? '#0066CC' : '#e0f2fe',
                color: msilLayers.size > 0 ? '#fff' : '#0369a1',
                transition: 'all .15s',
              }}
            >
              <Layers size={13} />
              海しる{msilLayers.size > 0 ? ` (${msilLayers.size})` : ''}
            </button>
            {showLayerMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 1998 }} onClick={() => setShowLayerMenu(false)} />
                <div style={{
                  position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 1999,
                  background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 12,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)', minWidth: 220, overflow: 'hidden',
                }}>
                  <div style={{ padding: '8px 14px 6px', fontSize: 11, fontWeight: 700, color: '#64748b', borderBottom: '1px solid #f1f5f9', letterSpacing: '0.05em' }}>
                    海しる(MSIL) レイヤー
                  </div>
                  {([
                    { group: '海底情報', items: [
                      { id: 'depth_contour', label: '等深線' },
                      { id: 'seabed_obs',    label: '海底障害物' },
                    ]},
                    { group: '標識・錨地', items: [
                      { id: 'light',        label: '灯台/灯浮標' },
                      { id: 'anchor_berth', label: '指定錨地' },
                    ]},
                    { group: '航路・区域', items: [
                      { id: 'traffic_route', label: '海交法航路' },
                      { id: 'port_passage',  label: '港則法航路' },
                      { id: 'port_area',     label: '港則法区域' },
                    ]},
                    { group: '航行情報', items: [
                      { id: 'nav_warning', label: '航行警報' },
                      { id: 'notices',     label: '水路通報' },
                    ]},
                    { group: '潮流推算', items: [
                      { id: 'tidal_seto', label: '瀬戸内海' },
                    ]},
                  ] as const).map(({ group, items }) => (
                    <div key={group}>
                      <div style={{ padding: '5px 14px 2px', fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.04em' }}>{group}</div>
                      {items.map(({ id, label }) => (
                        <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#374151' }}
                          onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = '#f8fafc'}
                          onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}
                        >
                          <input
                            type="checkbox"
                            checked={msilLayers.has(id)}
                            onChange={e => setMsilLayers(prev => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(id); else next.delete(id);
                              return next;
                            })}
                            style={{ accentColor: '#0066CC', width: 14, height: 14, cursor: 'pointer' }}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  ))}
                  <div style={{ borderTop: '1px solid #f1f5f9', padding: '6px 14px' }}>
                    <button
                      onClick={() => setMsilLayers(new Set())}
                      style={{ fontSize: 11, color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                    >すべてクリア</button>
                  </div>
                  {/* 海しるの出典・免責（msil_attribution）。
                      海洋状況表示システム利用規約 7-エ と公開API利用規約が求める表示。 */}
                  {hasFeature('msil_attribution') && (
                    <div style={{ borderTop: '1px solid #f1f5f9', padding: '8px 14px', background: '#f8fafc', fontSize: 10, lineHeight: 1.6, color: '#64748b' }}>
                      出典：
                      <a href="https://www.msil.go.jp/" target="_blank" rel="noopener noreferrer" style={{ color: '#0369a1' }}>
                        海しる（海洋状況表示システム）
                      </a>
                      を加工して作成<br />
                      海上保安庁が内容を保証するものではありません。航海用の海図・水路図誌に代わるものではないため、航行の判断には使用しないでください。
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* 寄港地 */}
          <button onClick={() => setShowPorts(p => !p)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            background: showPorts ? '#0891b2' : '#e0f2fe',
            color: showPorts ? '#fff' : '#0369a1',
            transition: 'all .15s',
          }}>
            ⚓ 寄港地{showPorts ? ' ✕' : ''}
          </button>

          {/* Windy 天気。windy_map が無効な会社では「準備中」のボタンだけ残し、
              機能が来ることは見せつつ実際の読み込みはしない。
              フラグを ON にすれば即座に元に戻る（手順は docs/WINDY_LICENSE.md）。 */}
          {windyEnabled ? (
            <button onClick={() => { setShowWindy(p => !p); setShowAis(false); }} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: showWindy ? '#0ea5e9' : '#e0f2fe',
              color: showWindy ? '#fff' : '#0369a1',
              transition: 'all .15s',
            }}>
              <Cloud size={13} /> Windy{showWindy ? ' ✕' : ''}
            </button>
          ) : (
            <button
              disabled
              title="気象レイヤーは提供準備中です"
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '5px 12px', border: '1px dashed #cbd5e1', borderRadius: 7, cursor: 'not-allowed',
                fontSize: 12, fontWeight: 600, background: '#f8fafc', color: '#94a3b8',
              }}
            >
              <Cloud size={13} /> Windy
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
                background: '#e2e8f0', color: '#64748b',
              }}>準備中</span>
            </button>
          )}

          {/* AIS 船舶追跡（MarineTraffic / VesselFinder） */}
          <button onClick={() => {
            setShowAis(p => {
              if (!p) {
                const sel = vessels.find(v => v.id === selectedId) ?? vessels[0];
                if (sel && sel.lat && sel.lng) setAisCenter({ lat: sel.lat, lng: sel.lng });
              }
              return !p;
            });
            setShowWindy(false);
          }} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 12px', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600,
            background: showAis ? '#7c3aed' : '#ede9fe',
            color: showAis ? '#fff' : '#6d28d9',
            transition: 'all .15s',
          }}>
            <Ship size={13} /> AIS{showAis ? ' ✕' : ''}
          </button>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {lastUpdated && (
            <span style={{ fontSize: 11, color: '#9CA3AF' }}>
              最終更新 {lastUpdated.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
          <button
            onClick={() => fetchVessels()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: '#374151', fontWeight: 600 }}
            onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >
            <RefreshCw size={13} /> 更新
          </button>
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? '全画面終了' : '全画面表示'}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 7, cursor: 'pointer', color: '#374151' }}
            onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
          >
            {isFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
          </button>
        </div>
      </div>

      {/* 船舶タブ */}
      <div style={{
        background: '#fff', borderBottom: '1px solid #E5E7EB',
        padding: '0 24px', display: 'flex', gap: 2, flexShrink: 0, overflowX: 'auto',
      }}>
        {loading ? (
          <div style={{ padding: '12px 0', color: '#9CA3AF', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} />読み込み中...
          </div>
        ) : vessels.length === 0 ? (
          <div style={{ padding: '12px 0', color: '#9CA3AF', fontSize: 13 }}>船舶データがありません</div>
        ) : vessels.map(v => {
          const isActive = selectedId === v.id;
          const isOnline = v.color !== '#94A3B8';
          const tabColor = isActive ? (isOnline ? '#0EA5E9' : '#64748B') : '#6B7280';
          const crewAtTime = vesselCrewAtTime[v.id] ?? [];
          const workingCount = crewAtTime.filter(c => c.status === 'working').length;
          return (
            <button key={v.id} onClick={() => setSelectedId(v.id)}
              style={{
                padding: '10px 18px', border: 'none',
                borderBottom: isActive ? `3px solid ${isOnline ? '#0EA5E9' : '#64748B'}` : '3px solid transparent',
                background: 'none', cursor: 'pointer',
                fontSize: 13, fontWeight: isActive ? 700 : 400,
                color: tabColor,
                whiteSpace: 'nowrap', transition: 'color 0.1s',
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.color = '#6B7280'; }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Circle size={7} fill={isOnline ? '#0EA5E9' : '#94A3B8'} strokeWidth={0} />
                {v.name}
                {workingCount > 0 && (
                  <span style={{ fontSize: 10, background: '#E0F2FE', color: '#0369a1', padding: '1px 6px', borderRadius: 20, fontWeight: 700 }}>
                    {workingCount}名勤務中
                  </span>
                )}
                {crewAtTime.length > 0 && workingCount === 0 && (
                  <span style={{ fontSize: 10, background: '#F1F5F9', color: '#64748B', padding: '1px 6px', borderRadius: 20, fontWeight: 700 }}>
                    {crewAtTime.length}名乗船
                  </span>
                )}
                {v.positionUnknown && (
                  <span style={{ fontSize: 10, background: '#FEF3C7', color: '#92400E', padding: '1px 6px', borderRadius: 20, fontWeight: 700 }}>
                    位置不明
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>

      {/* メインコンテンツ */}
      <div ref={mapWrapperRef} style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 300px', overflow: 'hidden', background: '#F4F6F8' }}>
        {/* 地図エリア */}
        <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <VesselTrailMap
              vessels={vesselData}
              selectedId={selectedId}
              currentTime={sliderTs}
              showTrail={showTrail}
              showLabel={showLabel}
              mapType={mapType}
              showMaritimeZones={showMaritimeZones}
              apiBase={apiBase}
              msilLayers={[...msilLayers]}
              ports={showPorts ? ports : []}
              hideUi={showWindy || showAis}
            />
            {/* Windy 天気オーバーレイ（windy_map が有効な会社のみ）。
                embed.windy.com の iframe は商用サイトで使えないため Map Forecast API 版を使う。
                自社の船・航跡・予測位置を気象レイヤーの上に直接描ける。 */}
            {windyEnabled && showWindy && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 500 }}>
                <WindyMap
                  vessels={vessels.map(v => ({
                    id: v.id, name: v.name, color: v.color,
                    lat: v.lat, lng: v.lng, speed: v.speed, heading: v.heading,
                    trail: v.trail.map(p => ({ ts: p.ts, lat: p.lat, lng: p.lng })),
                  }))}
                  currentTime={sliderTs}
                  visible={showWindy}
                />
              </div>
            )}
            {/* AIS 船舶追跡（外部サービスを新しいタブで開く）
                2026-08: VesselFinder が iframe 埋め込みを拒否するようになり（X-Frame-Options: DENY、
                /aismap は 400 Bad Request）、埋め込みでは「Forbidden」しか表示できなくなった。
                MarineTraffic の embed も ERR_BLOCKED_BY_RESPONSE で開けない。
                無料で画面内に出せる代替が無いため、選択中の船の位置を渡して外部サイトを開く形にする。
                自社GPS（打刻由来の航跡）はこの画面の地図でそのまま見られる。 */}
            {showAis && (
              <div style={{ position: 'absolute', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)' }}>
                <div style={{ background: '#fff', borderRadius: 18, padding: '26px 28px', maxWidth: 460, width: 'calc(100% - 48px)', boxShadow: '0 24px 60px rgba(0,0,0,0.28)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                    <Ship size={20} color="#0057d9" />
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>AIS 船舶追跡</div>
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.85, marginBottom: 18 }}>
                    他船を含むAIS情報は外部サービスの提供です。提供元が画面内への埋め込みを
                    停止したため、<b>新しいタブで開きます</b>。
                    {selectedVessel?.name && <><br />いま選択中の <b>{selectedVessel.name}</b> の位置を中心に開きます。</>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {AIS_PROVIDERS.map(p => (
                      <a key={p.label} href={p.url(aisCenter.lat, aisCenter.lng)} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 18px', borderRadius: 12, border: '1.5px solid #dbeafe', background: '#f8fbff', textDecoration: 'none' }}>
                        <span>
                          <span style={{ display: 'block', fontSize: 14.5, fontWeight: 800, color: '#0f172a' }}>{p.label}</span>
                          <span style={{ display: 'block', fontSize: 11.5, color: '#64748b', marginTop: 2 }}>{p.note}</span>
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#1d4ed8', flexShrink: 0 }}>開く ↗</span>
                      </a>
                    ))}
                  </div>
                  <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.8, marginTop: 16 }}>
                    自社船の位置・航跡（打刻とGPSログ由来）は、この画面の地図でそのまま確認できます。
                    画面内にAISを表示するには、AIS提供事業者の有料APIとの接続が必要です。
                  </div>
                  <button onClick={() => setShowAis(false)}
                    style={{ width: '100%', marginTop: 16, padding: '12px 0', borderRadius: 12, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                    閉じる
                  </button>
                </div>
              </div>
            )}
            {loading && (
              <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#64748b', gap: 8 }}>
                <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                GPS データ読み込み中...
              </div>
            )}
            {!loading && vessels.every(v => v.trail.length === 0) && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, pointerEvents: 'none' }}>
                <div style={{ background: 'rgba(255,255,255,0.94)', borderRadius: 14, padding: '28px 36px', textAlign: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                  <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}>
                    <Radio size={36} color="#94A3B8" />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1E293B', marginBottom: 6 }}>GPS データなし</div>
                  <div style={{ fontSize: 13, color: '#64748B', lineHeight: 1.7 }}>
                    スマホ（船員アプリ）をデバイス登録すると<br />ここに航跡が表示されます。
                  </div>
                  <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 6 }}>
                    管理画面 → デバイス管理 でトークンを発行
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* タイムラインスライダー */}
          <TimelineSlider
            min={rangeMin}
            max={rangeMax}
            value={sliderTs}
            onChange={setSliderTs}
            isPlaying={isPlaying}
            onPlayPause={() => setIsPlaying(p => !p)}
            speed={speed}
            onSpeedChange={setSpeed}
            period={period}
            onPeriodChange={setPeriod}
          />
        </div>

        {/* 乗組員パネル */}
        <CrewStatusPanel
          vessel={selectedVessel}
          currentTime={sliderTs}
          crew={dynamicCrew}
        />
      </div>


      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
